<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { Button } from '../button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../card';

export interface TourStep {
  id: string;
  title: string;
  text: string;
  attachTo?: {
    element: string;
    on: 'top' | 'bottom' | 'left' | 'right';
  };
  when?: {
    show: () => void;
  };
  buttons: {
    text: string;
    action: () => void;
    secondary?: boolean;
  }[];
}

const props = defineProps<{
  steps: TourStep[];
  active: boolean;
  defaultPosition?: 'center' | 'bottom-right';
}>();

const emit = defineEmits<{
  'update:active': [boolean];
  complete: [];
  cancel: [];
}>();

const currentStepIndex = ref(0);
const highlightStyle = ref<string | null>(null);
const highlightInterval = ref<number | null>(null);
const position = ref({ x: 0, y: 0 });
const tooltipPosition = ref<'top' | 'bottom' | 'left' | 'right'>('bottom');
const showTooltip = ref(false);

const currentStep = computed(() => props.steps[currentStepIndex.value]);

const isFirstStep = computed(() => currentStepIndex.value === 0);
const isLastStep = computed(() => currentStepIndex.value === props.steps.length - 1);
const currentStepNumber = computed(() => currentStepIndex.value + 1);
const totalSteps = computed(() => props.steps.length);

// Handle moving to next step
function next() {
  if (currentStepIndex.value < props.steps.length - 1) {
    currentStepIndex.value++;
    moveToStep(currentStepIndex.value);
  } else {
    complete();
  }
}

// Handle moving to previous step
function back() {
  if (currentStepIndex.value > 0) {
    currentStepIndex.value--;
    moveToStep(currentStepIndex.value);
  }
}

// Complete the tour
function complete() {
  cleanup();
  emit('complete');
  emit('update:active', false);
}

// Cancel the tour
function cancel() {
  cleanup();
  emit('cancel');
  emit('update:active', false);
}

// Clean up any resources
function cleanup() {
  if (highlightInterval.value) {
    clearInterval(highlightInterval.value);
    highlightInterval.value = null;
  }
  highlightStyle.value = null;
  showTooltip.value = false;
}

// Update highlight position for an element
function updateHighlight(selector: string) {
  const elem = document.querySelector(selector);
  if (elem) {
    const rect = elem.getBoundingClientRect();
    const margin = 8;
    let { width, height, top, left } = rect;

    top -= margin;
    height += margin * 2;
    left -= margin;
    width += margin * 2;

    highlightStyle.value = `width: ${width}px; height: ${height}px; top: ${top}px; left: ${left}px`;

    const attachment = currentStep.value.attachTo?.on || 'bottom';
    tooltipPosition.value = attachment;

    // Position tooltip based on attachment point
    switch (attachment) {
      case 'top':
        position.value = { x: left + width / 2, y: top };
        break;
      case 'bottom':
        position.value = { x: left + width / 2, y: top + height };
        break;
      case 'left':
        position.value = { x: left, y: top + height / 2 };
        break;
      case 'right':
        position.value = { x: left + width, y: top + height / 2 };
        break;
    }
  } else {
    highlightStyle.value = null;

    if (props.defaultPosition === 'bottom-right') {
      position.value = { x: window.innerWidth - 350, y: window.innerHeight - 350 };
    } else {
      position.value = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    }
  }
}

// Move to a specific step
function moveToStep(stepIndex: number) {
  if (stepIndex < 0 || stepIndex >= props.steps.length) {
    return;
  }

  // Hide tooltip during transition
  showTooltip.value = false;

  // Clear previous highlight interval
  if (highlightInterval.value) {
    clearInterval(highlightInterval.value);
    highlightInterval.value = null;
  }

  const step = props.steps[stepIndex];

  // Run any step-specific show logic
  if (step.when?.show) {
    step.when.show();
  }

  // For steps without attachTo, center in screen
  if (!step.attachTo) {
    highlightStyle.value = null;
    position.value = {
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    };
    tooltipPosition.value = 'bottom'; // Default position, but arrow will be hidden
    showTooltip.value = true;
    return;
  }

  // Special handling for specific steps
  if (step.id === 'sheet-overview') {
    // Clear any highlight during sheet transitions
    highlightStyle.value = null;

    // We need a timeout here to wait for the sheet to fully open
    setTimeout(() => {
      position.value = {
        x: window.innerWidth / 2,
        y: Math.max(250, window.innerHeight / 3),
      };
      showTooltip.value = true;
    }, 500); // Delay for sheet to open

    return;
  } else if (step.id === 'save-assignments') {
    // First ensure the element is in view
    const scrollToElement = document.querySelector(step.attachTo?.element || '.save-assignments-btn');
    if (scrollToElement) {
      scrollToElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    // Wait for scrolling and UI updates
    setTimeout(() => {
      if (step.attachTo) {
        const elem = document.querySelector(step.attachTo.element);
        if (elem) {
          const rect = elem.getBoundingClientRect();
          const margin = 8;

          // Set highlight style WITH the shadow overlay
          highlightStyle.value = `width: ${rect.width + margin * 2}px; height: ${rect.height + margin * 2}px; top: ${rect.top - margin}px; left: ${rect.left - margin}px;`;

          // Ensure the tooltip stays within viewport bounds
          // Calculate right edge position if we center the tooltip on the button
          const tooltipWidth = 450; // Max width of tooltip
          const halfTooltipWidth = tooltipWidth / 2;
          const buttonCenterX = rect.left + rect.width / 2;

          // If centering would push tooltip off right edge, adjust x position
          let tooltipX = buttonCenterX;
          if (buttonCenterX + halfTooltipWidth > window.innerWidth - 20) {
            tooltipX = window.innerWidth - halfTooltipWidth - 20; // 20px margin
          }

          position.value = {
            x: tooltipX,
            y: rect.top - 20, // Position above the button
          };
          tooltipPosition.value = 'top';
        }

        // Set up interval to keep highlight in correct position WITH shadow overlay
        highlightInterval.value = window.setInterval(() => {
          if (step.attachTo) {
            const elem = document.querySelector(step.attachTo.element);
            if (elem) {
              const rect = elem.getBoundingClientRect();
              const margin = 8;
              highlightStyle.value = `width: ${rect.width + margin * 2}px; height: ${rect.height + margin * 2}px; top: ${rect.top - margin}px; left: ${rect.left - margin}px;`;
            }
          }
        }, 300);
      }

      showTooltip.value = true;
    }, 400);

    return;
  }

  // For all other steps, use nextTick for better performance
  nextTick(() => {
    if (step.attachTo) {
      updateHighlight(step.attachTo.element);
      highlightInterval.value = window.setInterval(() => {
        if (step.attachTo) {
          updateHighlight(step.attachTo.element);
        }
      }, 500);
    } else {
      highlightStyle.value = null;

      if (props.defaultPosition === 'bottom-right') {
        position.value = { x: window.innerWidth - 350, y: window.innerHeight - 350 };
      } else {
        position.value = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
      }
    }

    // Show tooltip immediately using nextTick instead of timeout
    showTooltip.value = true;
  });
}

// Handle button click based on whether it has a real action or needs default navigation
function handleButtonClick(button: { text: string; action: () => void; secondary?: boolean }) {
  const actionStr = button.action.toString();

  // Special timing only needed for sheet transitions
  if (button.text === 'Show Product Selection') {
    button.action();

    // This timeout is necessary to ensure the sheet has opened before advancing
    setTimeout(() => {
      next();
    }, 600);
    return;
  }

  if (button.text === 'Close Sheet') {
    button.action();

    // Wait for the sheet to close before advancing
    setTimeout(() => {
      next();
    }, 600);
    return;
  }

  // Special case for currentTourStep.value setting actions
  // This handles steps that just update the currentTourStep value
  if (actionStr.includes('currentTourStep.value')) {
    button.action();

    // Also advance to the next step in the tour sequence
    if (button.text.toLowerCase().includes('next')) {
      next();
    } else if (button.text.toLowerCase().includes('back')) {
      back();
    }
    return;
  }

  // For empty action functions, handle navigation based on button text
  if (actionStr.replace(/\s+/g, '') === '()=>{}') {
    if (
      button.text.toLowerCase().includes('next') ||
      button.text.toLowerCase().includes('continue') ||
      button.text.toLowerCase().includes('finish')
    ) {
      next();
    } else if (button.text.toLowerCase().includes('back')) {
      back();
    }
  } else {
    // For non-empty actions, execute the provided function
    button.action();
  }
}

// Start tour when active prop changes
watch(
  () => props.active,
  (isActive) => {
    if (isActive) {
      currentStepIndex.value = 0;
      moveToStep(0);
    } else {
      cleanup();
    }
  },
  { immediate: true },
);

// Handle window resize
function handleResize() {
  if (props.active && currentStep.value?.attachTo) {
    updateHighlight(currentStep.value.attachTo.element);
  }
}

// Set up and clean up event listeners
onMounted(() => {
  window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
  cleanup();
});
</script>

<template>
  <Teleport to="body">
    <!-- Backdrop overlay -->
    <div v-if="active" class="fixed inset-0 z-100000" @click.self="cancel"></div>

    <!-- Highlight for current element -->
    <div
      v-if="active && highlightStyle"
      class="pointer-events-none absolute z-100001 rounded-sm border-2 border-blue-500"
      :class="{ 'shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]': !highlightStyle.includes('box-shadow: none') }"
      :style="highlightStyle"
    ></div>

    <!-- Tour tooltip using shadcn Card -->
    <Card
      v-if="active && showTooltip && currentStep"
      class="pointer-events-auto fixed z-100002 w-full max-w-md shadow-lg duration-100"
      :style="{
        border: 'none',
        transform: 'translate(-50%, -50%)',
        left: `${position.x}px`,
        top: `${position.y}px`,
        ...(currentStep.attachTo
          ? tooltipPosition === 'bottom'
            ? { transform: 'translate(-50%, 20px)' }
            : tooltipPosition === 'top'
              ? { transform: 'translate(-50%, -120%)' }
              : tooltipPosition === 'left'
                ? { transform: 'translate(-120%, -50%)' }
                : tooltipPosition === 'right'
                  ? { transform: 'translate(20px, -50%)' }
                  : {}
          : {}),
      }"
    >
      <!-- Tooltip arrow - only show if step has an attachTo property -->
      <div
        v-if="currentStep.attachTo"
        class="absolute z-100001 h-4 w-4 rotate-45 bg-white"
        :class="{
          'top-0 left-1/2 -translate-x-1/2 -translate-y-1/2': tooltipPosition === 'bottom',
          'bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2': tooltipPosition === 'top',
          'top-1/2 right-0 translate-x-1/2 -translate-y-1/2': tooltipPosition === 'left',
          'top-1/2 left-0 -translate-x-1/2 -translate-y-1/2': tooltipPosition === 'right',
        }"
      ></div>

      <CardHeader>
        <div class="flex items-center justify-between">
          <CardTitle>{{ currentStep.title }}</CardTitle>
          <button @click="cancel">
            <i class="fa-light fa-times text-lg"></i>
          </button>
        </div>
      </CardHeader>

      <CardContent class="p-4">
        <CardDescription class="text-base text-slate-700">{{ currentStep.text }}</CardDescription>
      </CardContent>

      <CardFooter class="flex items-center justify-between border-t p-4">
        <div class="text-sm text-slate-500">Step {{ currentStepNumber }} of {{ totalSteps }}</div>
        <div class="flex space-x-2">
          <template v-if="currentStep.buttons && currentStep.buttons.length">
            <Button
              v-for="(button, idx) in currentStep.buttons"
              :key="idx"
              :variant="button.secondary ? 'outline' : 'default'"
              @click="handleButtonClick(button)"
            >
              {{ button.text }}
            </Button>
          </template>

          <template v-else>
            <Button v-if="!isFirstStep" variant="outline" @click="back"> Back </Button>
            <Button @click="next">
              {{ isLastStep ? 'Finish' : 'Next' }}
            </Button>
          </template>
        </div>
      </CardFooter>
    </Card>
  </Teleport>
</template>
